import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';

const ImageSlider = ({ campaign }) => {

  const domain = useMemo(() => 
    `https://api.poster.realtyhub.cy/`, 
  []);

  const images = useMemo(() => {
    if (campaign?.number_of_files?.length > 0 && campaign.file_type === "photo") {
      return campaign.number_of_files.map(file => ({
        src: `${domain}${file}`,
        preloaded: false
      }));
    }
    return [{
      src: "https://user-images.githubusercontent.com/20684618/31289519-9ebdbe1a-aae6-11e7-8f82-bf794fdd9d1a.png",
      preloaded: true
    }];
  }, [campaign, domain]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('');
  const [loadedImages, setLoadedImages] = useState(new Set());


  useEffect(() => {
    const preloadImages = images.map((img, index) => {
      if (!img.preloaded && !loadedImages.has(img.src)) {
        const image = new Image();
        image.src = img.src;
        image.onload = () => {
          setLoadedImages(prev => new Set([...prev, img.src]));
        };
        return image;
      }
      return null;
    });

    return () => preloadImages.forEach(img => img?.onload && (img.onload = null));
  }, [images, loadedImages]);

  const navigate = useCallback((direction) => {
    setSlideDirection(direction);
    requestAnimationFrame(() => {
      setCurrentIndex(prev => {
        if (direction === 'next') return (prev + 1) % images.length;
        return (prev - 1 + images.length) % images.length;
      });
      requestAnimationFrame(() => setSlideDirection(''));
    });
  }, [images.length]);

  const nextSlide = useCallback(() => images.length > 1 && navigate('next'), [images.length, navigate]);
  const prevSlide = useCallback(() => images.length > 1 && navigate('prev'), [images.length, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  const currentImage = images[currentIndex]?.src;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '400px',
      overflow: 'hidden',
      willChange: 'transform'
    }}>
      <Typography
        variant="caption"
        sx={{
        marginRight:'10px',
          position: 'absolute',
          zIndex: 1,
          bottom: 8,
          left: 8,
          backgroundColor: 'rgba(31, 31, 31, 0.25)',
          backdropFilter: 'blur(2px)',
          padding: '4px 8px',
          color: '#fff',
          borderRadius: '5px',
          boxShadow: '0 4px 8px rgba(37, 37, 37, 0.2)',
          fontWeight: 'bold',
          fontSize: '14px'
        }}
      >
        {campaign.message_header || "Untitled Message"}
      </Typography>
      <Box sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {images.map((img, index) => (
          <Box
            key={img.src}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: `translateX(${(index - currentIndex) * 100}%)`,
              willChange: 'transform',
              ...(loadedImages.has(img.src)) ? {} : {
                background: 'linear-gradient(135deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 200%',
                animation: 'shimmer 2s infinite linear'
              }
            }}
          >
            {loadedImages.has(img.src) && (
              <img
                src={img.src}
                alt="Message Preview"
                loading={index === currentIndex ? 'eager' : 'lazy'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  willChange: 'transform'
                }}
              />
            )}
          </Box>
        ))}
      </Box>
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          background: 'linear-gradient(135deg, rgb(41, 41, 41), rgb(110, 110, 110))',
          color: '#fff',
          padding: '6px 20px',
          borderTopLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          fontWeight: 700,
          fontSize: '0.75rem',
          backdropFilter: 'blur(2px)',
          textTransform: 'uppercase',
          boxShadow: '0 4px 10px rgba(138, 138, 138, 0.3)',
          letterSpacing: '0.8px'
        }}
      >
        🎯{campaign.id || "Special"}
      </Typography>
      {images.length > 1 && (
        <>
          <IconButton
            sx={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', color: 'white' }}
            onClick={prevSlide}
          >
            <ArrowBackIos />
          </IconButton>
          <IconButton
            sx={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', color: 'white' }}
            onClick={nextSlide}
          >
            <ArrowForwardIos />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default ImageSlider;
